import { type ButtonHTMLAttributes } from 'react'
import clsx from 'clsx'
import { Button as DsButton } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'

// ---------------------------------------------------------------------------
// Button — presentational wrapper.
//
// Designsystemet owns appearance; legacy classes remain layout hooks.
//
// Props:
//   variant   — visual role: primary | default | ghost | danger
//   size      — md inherits the selected density | sm uses the small size mode
//   icon      — icon to display (Icon name); placed before children
//   iconOnly  — square icon-only button (.btn--icon); children are omitted
//   disabled  — delegates to the native disabled attribute
//   type      — 'button' | 'submit' | 'reset'; defaults to 'button' to
//               prevent accidental form submissions
//   onClick   — click handler
//   className — additional CSS classes merged after the .btn classes
//
// Passes through any remaining <button> HTML attributes via ...rest.
// ---------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'default' | 'ghost' | 'danger'
export type ButtonSize = 'md' | 'sm'

export type ButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconName
  iconOnly?: boolean
  children?: React.ReactNode
  className?: string
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>

export function Button({
  variant = 'default',
  size = 'md',
  icon,
  iconOnly = false,
  children,
  className,
  type = 'button',
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  const classes = clsx(
    'btn',
    variant === 'primary' && 'btn--primary',
    variant === 'ghost' && 'btn--ghost',
    variant === 'danger' && 'btn--danger',
    size === 'sm' && 'btn--sm',
    iconOnly && 'btn--icon',
    className,
  )

  return (
    <DsButton
      variant={variant === 'primary' ? 'primary' : variant === 'ghost' ? 'tertiary' : 'secondary'}
      data-color={variant === 'danger' ? 'danger' : undefined}
      data-size={size === 'sm' ? 'sm' : undefined}
      icon={iconOnly}
      type={type}
      className={classes}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      {...rest}
    >
      {icon != null && (
        <Icon
          name={icon}
          size={size === 'sm' ? 14 : 16}
          aria-hidden="true"
        />
      )}
      {!iconOnly && children}
    </DsButton>
  )
}
